import { useState, useRef } from 'react';
import type { DragEvent } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Upload, X, Star, AlertCircle, Image as ImageIcon, Camera } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface AssetPhoto {
  id: string;
  url: string;
  isPrimary: boolean;
  uploadedDate: string;
  fileName?: string;
  fileSize?: number;
}

interface AssetDetailPhotosProps {
  readonly photos: AssetPhoto[];
  readonly onChange: (photos: AssetPhoto[]) => void;
  readonly disabled?: boolean;
  readonly maxPhotos?: number;
}

export function AssetDetailPhotos({ 
  photos, 
  onChange, 
  disabled = false,
  maxPhotos = 5 
}: Readonly<AssetDetailPhotosProps>): React.ReactElement {
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const remainingSlots = maxPhotos - photos.length;
  const canUploadMore = photos.length < maxPhotos;

  const validateFile = (file: File): string | null => {
    const allowedTypes = ['image/gif', 'image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      return 'Invalid file type. Please upload a GIF, PNG, JPG, or WEBP image.';
    }

    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      return 'File size exceeds 5MB. Please upload a smaller image.';
    }

    return null;
  };

  const handleFileSelect = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    setError(null);

    if (photos.length + files.length > maxPhotos) {
      setError(`Cannot upload ${files.length} photo(s). You can only upload ${remainingSlots} more photo(s).`);
      return;
    }

    const newPhotos: AssetPhoto[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      const validationError = validateFile(file);
      if (validationError) {
        setError(validationError);
        continue;
      }

      try {
        const dataUrl = await readFileAsDataURL(file);
        
        const newPhoto: AssetPhoto = {
          id: `photo-${Date.now()}-${i}`,
          url: dataUrl,
          isPrimary: photos.length === 0 && newPhotos.length === 0,
          uploadedDate: new Date().toISOString(),
          fileName: file.name,
          fileSize: file.size,
        };

        newPhotos.push(newPhoto);
      } catch (err) {
        console.error('Error reading file:', err);
        setError('Failed to read file. Please try again.');
      }
    }

    if (newPhotos.length > 0) {
      onChange([...photos, ...newPhotos]);
    }
  };

  const readFileAsDataURL = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleDragOver = (e: DragEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled && canUploadMore) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: DragEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: DragEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (disabled || !canUploadMore) return;

    const files = e.dataTransfer.files;
    handleFileSelect(files);
  };

  const handleDeletePhoto = (photoId: string) => {
    const updatedPhotos = photos.filter(p => p.id !== photoId);
    
    const hadPrimary = photos.find(p => p.id === photoId)?.isPrimary;
    if (hadPrimary && updatedPhotos.length > 0) {
      updatedPhotos[0].isPrimary = true;
    }

    onChange(updatedPhotos);
  };

  const handleSetPrimary = (photoId: string) => {
    const updatedPhotos = photos.map(p => ({
      ...p,
      isPrimary: p.id === photoId,
    }));
    onChange(updatedPhotos);
  };

  return (
    <Card className="border border-border/40">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Camera className="h-5 w-5" />
          Photos
        </CardTitle>
        <CardDescription className="text-xs">
          Upload images for visual identification and documentation
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="text-xs">{error}</AlertDescription>
          </Alert>
        )}

        {/* Upload Area - Compact */}
        {canUploadMore && !disabled && (
          <button
            type="button"
            className={cn(
              "border-2 border-dashed rounded-lg p-4 text-center transition-colors cursor-pointer w-full",
              isDragging 
                ? "border-primary bg-primary/5" 
                : "border-border hover:border-primary/50 hover:bg-muted/50"
            )}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            aria-label="Upload photos"
          >
            <Upload className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
            <p className="text-xs text-muted-foreground mb-1">
              Drop photos here or click to upload
            </p>
            <p className="text-xs text-muted-foreground">
              {remainingSlots} slot{remainingSlots !== 1 ? 's' : ''} remaining
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/gif,image/png,image/jpeg,image/jpg,image/webp"
              multiple
              onChange={(e) => handleFileSelect(e.target.files)}
              className="hidden"
            />
          </button>
        )}

        {/* Photos Grid - Compact */}
        {photos.length > 0 && (
          <div className="grid grid-cols-2 gap-2">
            {photos.map((photo) => (
              <div
                key={photo.id}
                className="relative group aspect-square rounded-lg overflow-hidden border border-border bg-muted"
              >
                <img
                  src={photo.url}
                  alt={photo.fileName ?? 'Asset photo'}
                  className="w-full h-full object-cover"
                />
                
                {/* Primary Badge */}
                {photo.isPrimary && (
                  <div className="absolute top-1 left-1">
                    <Badge variant="default" className="text-xs px-1.5 py-0">
                      <Star className="h-2.5 w-2.5 mr-0.5 fill-current" />
                      Primary
                    </Badge>
                  </div>
                )}

                {/* Actions Overlay */}
                {!disabled && (
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                    {!photo.isPrimary && (
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => handleSetPrimary(photo.id)}
                        className="h-7 text-xs"
                      >
                        <Star className="h-3 w-3 mr-1" />
                        Primary
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDeletePhoto(photo.id)}
                      className="h-7 w-7 p-0"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {photos.length === 0 && disabled && (
          <div className="text-center py-8 text-muted-foreground">
            <ImageIcon className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No photos uploaded</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
